import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import RichTextPreview from '../../../richTextPreview';
import ReorderItems from '../../../reorderItems';
import UserAvatar from '../../../userAvatar';
import ActionButton from '../../../actionButton';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';
import {
  getOrderedRankingOptions,
  getRankingOptionIds,
} from '../../../../../utility/fillSurveyUtils';
import { reorder } from '../../../../../utility/helpers';
import { useCustomEffect } from '../../../../../utility/hooks';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: spacing(4),
      '&:last-of-type': {
        marginBottom: 0,
      },
      [breakpoints.up('sm')]: {
        marginBottom: spacing(6),
      },
    },
    rootHistory: {
      cursor: 'default',
    },
    subject: {
      marginBottom: spacing(2),
      [breakpoints.up('sm')]: {
        marginBottom: spacing(4),
      },
    },
    userAvatar: {
      height: 20,
      width: 20,
      fontSize: 7,
      lineHeight: '18px',
      [breakpoints.up('sm')]: {
        fontSize: 14,
        lineHeight: 1,
        height: 32,
        width: 32,
      },
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 12,
      lineHeight: '14px',
      whiteSpace: 'nowrap',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
    item: {
      marginBottom: spacing(2),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    answerContent: {
      boxSizing: 'border-box',
      backgroundColor: primary.white,
      border: `1px solid ${primary.bluish6}`,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'flex-start',
      minWidth: 0,
      padding: spacing(2),
      userSelect: 'none',
      cursor: ({ isDisabled }) => (isDisabled ? 'default' : 'grab'),
      [breakpoints.up('sm')]: {
        padding: spacing(4),
      },
    },
    answerContentHistory: {
      backgroundColor: primary.bluish9,
    },
    answerContentDragging: {
      cursor: 'grabbing',
    },
    dragButton: {
      position: 'relative',
      width: 22,
      height: 22,
      flexShrink: 0,
      marginRight: spacing(1.5),
      '&&': {
        cursor: 'inherit',
      },
      [breakpoints.up('sm')]: {
        marginRight: spacing(2),
      },
      '& button': {
        width: 22,
        height: 22,
        pointerEvents: 'none',
      },
    },
    dragIcon: {
      width: 8,
      height: 14,
    },
    dragTooltipLabel: {
      fontFamily: 'ProximaNova-Regular',
    },
    answerText: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: 14,
      lineHeight: '16px',
      alignSelf: 'center',
      wordWrap: 'break-word',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
  })
);

const RankingItem = ({
  id,
  translations,
  option,
  isDisabled = false,
  isVisible = true,
  isHistoryView,
  dragRef = () => {},
  dragProps = null,
}) => {
  const classes = useStyles({ isDisabled });

  return (
    <div
      id={id}
      ref={dragRef}
      {...(dragProps ? { ...dragProps.draggableProps } : {})}
      {...(dragProps && !isDisabled ? { ...dragProps.dragHandleProps } : {})}
    >
      <div
        className={classNames(classes.answerContent, {
          [classes.answerContentDragging]: !isDisabled && !isVisible,
          [classes.answerContentHistory]: isHistoryView,
        })}
      >
        <ActionButton
          className={classes.dragButton}
          iconClass={classes.dragIcon}
          tooltipLabelClass={classes.dragTooltipLabel}
          type={ACTION_BUTTON_TYPES.DRAG}
          tooltipText={isDisabled ? '' : translations.dragTooltip}
          isDisabled={isDisabled}
        />
        <RichTextPreview
          text={option.text}
          className={classes.answerText}
          variant="body1"
        />
      </div>
    </div>
  );
};

RankingItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  translations: PropTypes.shape({}).isRequired,
  option: PropTypes.shape({}).isRequired,
  isDisabled: PropTypes.bool,
  isVisible: PropTypes.bool,
  dragRef: PropTypes.func,
  dragProps: PropTypes.object,
};

const Ranking = ({
  translations,
  questionId,
  questionType,
  subject,
  options = [],
  rankedOptionIds = [],
  isSingleReview = false,
  isDisabled = false,
  isHistoryView = false,
  onChange = () => {},
  onDraftChange = () => {},
}) => {
  const classes = useStyles({ isDisabled });
  const [orderedOptions, setOrderedOptions] = useState(() =>
    getOrderedRankingOptions(options, rankedOptionIds)
  );

  const reportDraft = useCallback(
    currentOptions => {
      onDraftChange(questionId, subject, getRankingOptionIds(currentOptions));
    },
    [onDraftChange, questionId, subject]
  );

  useEffect(() => {
    reportDraft(orderedOptions);
  }, [orderedOptions, reportDraft]);

  useCustomEffect(
    () => {
      const nextOptions = getOrderedRankingOptions(options, rankedOptionIds);

      setOrderedOptions(nextOptions);
    },
    [questionId],
    false
  );

  const handleReorder = useCallback(
    result => {
      if (
        isDisabled ||
        !result.destination ||
        result.source.index === result.destination.index
      ) {
        return;
      }

      const nextOptions = reorder(
        orderedOptions,
        result.source.index,
        result.destination.index
      );

      setOrderedOptions(nextOptions);
      onChange(
        questionId,
        questionType,
        getRankingOptionIds(nextOptions),
        subject
      );
    },
    [isDisabled, orderedOptions, onChange, questionId, questionType, subject]
  );

  return (
    <div
      className={classNames(classes.root, {
        [classes.rootHistory]: isHistoryView,
      })}
    >
      {!isSingleReview && (
        <UserAvatar
          className={classes.subject}
          customAvatarClass={classes.userAvatar}
          labelClass={classes.userLabel}
          user={subject}
          caption
        />
      )}
      <ReorderItems
        itemClass={classes.item}
        droppableId={`ranking_${subject.review_tracking}`}
        isDragDisabled={isDisabled}
        onSave={handleReorder}
      >
        {orderedOptions.map(option => (
          <RankingItem
            key={`ranking_item_${option.id}`}
            id={option.id}
            translations={translations}
            option={option}
            isDisabled={isDisabled}
            isHistoryView={isHistoryView}
          />
        ))}
      </ReorderItems>
    </div>
  );
};

Ranking.propTypes = {
  translations: PropTypes.shape({}).isRequired,
  questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  questionType: PropTypes.string.isRequired,
  subject: PropTypes.shape({}).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  rankedOptionIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  isSingleReview: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isHistoryView: PropTypes.bool,
  onChange: PropTypes.func,
  onDraftChange: PropTypes.func,
};

export default Ranking;
